import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import Watermark from "../components/Watermark";
import CommentsPanel from "../components/CommentsPanel";
import StatusFilterCards from "../components/StatusFilterCards";
import SubtaskDrawer from "../components/SubtaskDrawer";
import useServerEvents from "../hooks/useServerEvents";
import { getMinDate, validateDueDate } from "../utils/dateUtils";

const PRIORITIES = ["Low", "Medium", "High"];

function addBusinessDays(date, days) {
  let result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0 && result.getDay() !== 6) added++;
  }
  return result;
}

const priorityBadge = (p) =>
  p === "High" ? "bg-red-100 text-red-700" :
    p === "Medium" ? "bg-yellow-100 text-yellow-700" :
      "bg-green-100 text-green-700";

const statusBadge = (s) =>
  s === "Open" ? "bg-blue-100 text-blue-700" :
    s === "Ongoing" ? "bg-yellow-100 text-yellow-700" :
      s === "Blocked" ? "bg-red-100 text-red-700" :
        s === "On Hold" ? "bg-orange-100 text-orange-700" :
          "bg-gray-100 text-gray-500";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem('employee_dashboard_tab') || 'assigned'
  );
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [adminTasks, setAdminTasks] = useState([]);
  const [createdTasks, setCreatedTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "Low", due_date: "" });
  const [createMsg, setCreateMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [assignedSearch, setAssignedSearch] = useState("");
  const [assignedStatus, setAssignedStatus] = useState("open");
  const [assignedPriority, setAssignedPriority] = useState("");
  const [assignedPage, setAssignedPage] = useState(1);
  const [assignedSort, setAssignedSort] = useState("newest");
  const ASSIGNED_PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);

  const [subtaskStats, setSubtaskStats] = useState(null);
  const [assignedSubtasks, setAssignedSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [activeSubtask, setActiveSubtask] = useState(null); // subtask open in drawer
  const [subtaskSearch, setSubtaskSearch] = useState("");
  const [subtaskStatusFilter, setSubtaskStatusFilter] = useState("Open");
  const [subtaskPriorityFilter, setSubtaskPriorityFilter] = useState("");
  const [subtaskPage, setSubtaskPage] = useState(1);
  const [subtaskSort, setSubtaskSort] = useState("newest");
  const SUBTASK_PAGE_SIZE = 10;
  const [reqSearch, setReqSearch] = useState("");
  const [reqStatusFilter, setReqStatusFilter] = useState("Open");
  const [reqPriorityFilter, setReqPriorityFilter] = useState("");
  const [reqPage, setReqPage] = useState(1);
  const [reqSort, setReqSort] = useState("newest");
  const REQ_PAGE_SIZE = 10;
  const [projectPeerTasks, setProjectPeerTasks] = useState([]);
  const [loadingPeerTasks, setLoadingPeerTasks] = useState(false);
  const [peerSearch, setPeerSearch] = useState("");
  const [peerStatusFilter, setPeerStatusFilter] = useState("");
  const [peerPriority, setPeerPriority] = useState("");
  const [peerPage, setPeerPage] = useState(1);
  const [peerSort, setPeerSort] = useState("newest");
  const PEER_PAGE_SIZE = 10;
  const [peerCommentsTask, setPeerCommentsTask] = useState(null);
  const [peerUnreadCounts, setPeerUnreadCounts] = useState({});
  const [requestUnreadCounts, setRequestUnreadCounts] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("jwt") || localStorage.getItem("token");

  // decode userId from JWT to filter out self-assigned tasks
  const currentUserId = (() => {
    try {
      return JSON.parse(atob(token.split(".")[1])).userId;
    } catch { return null; }
  })();

  useEffect(() => {
    fetchAllTasks();
    fetchSubtaskData();
    fetchProjectPeerTasks();
    const interval = setInterval(() => { fetchAllTasks(); fetchSubtaskData(); fetchProjectPeerTasks(); }, 30 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useServerEvents({
    onTaskCreated: () => fetchAllTasks(),
    onTaskUpdated: () => fetchAllTasks(),
    onCommentUpdated: ({ taskId }) => {
      // Refresh unread count for the affected peer task
      if (taskId) {
        const t = projectPeerTasks.find(pt => String(pt.id) === String(taskId));
        if (t) fetchPeerUnreadCounts([t]);
      }
    },
  }, token);

  const fetchPeerUnreadCounts = async (tasks) => {
    try {
      const map = {};
      await Promise.all((tasks || []).map(async (t) => {
        try {
          const r = await axios.get(`${apiUrl}/tasks/${t.id}/comments/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
          map[t.id] = r.data?.unread || 0;
        } catch { map[t.id] = 0; }
      }));
      setPeerUnreadCounts(prev => ({ ...prev, ...map }));
    } catch { /* ignore */ }
  };

  const fetchProjectPeerTasks = async () => {
    setLoadingPeerTasks(true);
    try {
      const res = await axios.get(`${apiUrl}/tasks/project-peers`, { headers: { Authorization: `Bearer ${token}` } });
      setProjectPeerTasks(res.data || []);
      setTimeout(() => fetchPeerUnreadCounts(res.data || []), 0);
    } catch {
      // silent
    } finally {
      setLoadingPeerTasks(false);
    }
  };

  const fetchSubtaskData = async () => {
    setLoadingSubtasks(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${apiUrl}/subtasks/stats/employee`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/subtasks/assigned/me`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSubtaskStats(statsRes.data);
      setAssignedSubtasks(listRes.data || []);
    } catch {
      // silent
    } finally {
      setLoadingSubtasks(false);
    }
  };

  const fetchAllTasks = async () => {
    setLoading(true);
    try {
      const tasksRes = await axios.get(`${apiUrl}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      const sorted = (tasksRes.data || []).slice().sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      // Merge PROJECT, ADMIN_TASK, and ADMIN_REQUEST (when reassigned to employee) into one list
      const assigned = sorted.filter(t =>
        (t.task_type_code === "PROJECT" || t.task_type_code === "ADMIN_TASK" || t.task_type_code === "ADMIN_REQUEST") &&
        Number(t.assigned_to) === Number(currentUserId)
      );
      setAssignedTasks(assigned);
      setAdminTasks(assigned);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
    // fetch submitted requests independently so a failure here doesn't blank assigned tasks
    try {
      const requestsRes = await axios.get(`${apiUrl}/tasks/my-requests`, { headers: { Authorization: `Bearer ${token}` } });
      const requests = requestsRes.data || [];
      setCreatedTasks(requests);
      // Fetch unread comment counts for requests (fire-and-forget)
      if (requests.length > 0) {
        Promise.all(requests.map(async (t) => {
          try {
            const r = await axios.get(`${apiUrl}/tasks/${t.id}/comments/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
            return [t.id, r.data?.unread || 0];
          } catch { return [t.id, 0]; }
        })).then(pairs => {
          const map = {};
          pairs.forEach(([id, cnt]) => { map[id] = cnt; });
          setRequestUnreadCounts(map);
        });
      }
    } catch (err) {
      // silent
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (submitting) return false;
    if (!newTask.title || !newTask.description) { setCreateMsg("Title and description are required."); return false; }
    const due_date = newTask.due_date || null;
    const dateErr = validateDueDate(due_date);
    if (dateErr) { setCreateMsg(dateErr); return false; }
    setSubmitting(true);
    try {
      await axios.post(`${apiUrl}/tasks`, { ...newTask, due_date }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Request submitted successfully!");
      setNewTask({ title: "", description: "", priority: "Low", due_date: "" });
      setCreateMsg("");
      setShowRequestForm(false);
      // Switch to the Submitted Requests tab so the new request is immediately visible
      setActiveTab("requests");
      sessionStorage.setItem("employee_dashboard_tab", "requests");
      fetchAllTasks();
      return true;
    } catch (err) {
      setCreateMsg(err.response?.data?.error || "Error submitting request.");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const parseLocalDate = (d) => { const datePart = String(d).split('T')[0]; const [y, m, day] = datePart.split('-').map(Number); return new Date(y, m - 1, day); };
  // API returns Title Case status names (Open, Ongoing, Blocked, On Hold, Closed)
  // Filter keys use legacy lowercase (open, in_progress, blocked, on_hold, closed)
  const STATUS_MAP = { open: "Open", in_progress: "Ongoing", blocked: "Blocked", on_hold: "On Hold", closed: "Closed" };
  const isNotClosed = (t) => t.status !== "Closed";

  const SORT_FN = {
    newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    oldest: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    due_asc: (a, b) => new Date(a.due_date || '9999') - new Date(b.due_date || '9999'),
    due_desc: (a, b) => new Date(b.due_date || '0') - new Date(a.due_date || '0'),
    title_az: (a, b) => (a.title || '').localeCompare(b.title || ''),
    title_za: (a, b) => (b.title || '').localeCompare(a.title || ''),
  };

  const filtered = assignedTasks
    .filter(t => !assignedSearch || t.title.toLowerCase().includes(assignedSearch.toLowerCase()))
    .filter(t => !assignedPriority || t.priority === assignedPriority)
    .filter(t => {
      if (!assignedStatus) return true;
      if (assignedStatus === "due_today") return t.due_date && parseLocalDate(t.due_date).toDateString() === today.toDateString() && isNotClosed(t);
      if (assignedStatus === "overdue") return t.due_date && parseLocalDate(t.due_date) < today && isNotClosed(t);
      return t.status === STATUS_MAP[assignedStatus];
    })
    .slice().sort(SORT_FN[assignedSort] || SORT_FN.newest);
  const assignedTotalPages = Math.max(1, Math.ceil(filtered.length / ASSIGNED_PAGE_SIZE));
  const pagedAssigned = filtered.slice((assignedPage - 1) * ASSIGNED_PAGE_SIZE, assignedPage * ASSIGNED_PAGE_SIZE);

  const filteredSubtasks = assignedSubtasks
    .filter(s => !subtaskSearch || s.title.toLowerCase().includes(subtaskSearch.toLowerCase()) || (s.parent_task_title || '').toLowerCase().includes(subtaskSearch.toLowerCase()))
    .filter(s => !subtaskStatusFilter || s.status === subtaskStatusFilter)
    .filter(s => !subtaskPriorityFilter || s.priority === subtaskPriorityFilter)
    .slice().sort(SORT_FN[subtaskSort] || SORT_FN.newest);

  const subtaskTotalPages = Math.max(1, Math.ceil(filteredSubtasks.length / SUBTASK_PAGE_SIZE));
  const pagedSubtasks = filteredSubtasks.slice((subtaskPage - 1) * SUBTASK_PAGE_SIZE, subtaskPage * SUBTASK_PAGE_SIZE);

  const filteredRequests = createdTasks
    .filter(t => !reqSearch || t.title.toLowerCase().includes(reqSearch.toLowerCase()) || (t.description || '').toLowerCase().includes(reqSearch.toLowerCase()))
    .filter(t => !reqStatusFilter || t.status === reqStatusFilter)
    .filter(t => !reqPriorityFilter || t.priority === reqPriorityFilter)
    .slice().sort(SORT_FN[reqSort] || SORT_FN.newest);
  const reqTotalPages = Math.max(1, Math.ceil(filteredRequests.length / REQ_PAGE_SIZE));
  const pagedRequests = filteredRequests.slice((reqPage - 1) * REQ_PAGE_SIZE, reqPage * REQ_PAGE_SIZE);

  // ── Tab definitions ───────────────────────────────────────────
  const tabs = [
    { key: "assigned", label: "My Assigned Tasks", count: assignedTasks.length },
    { key: "project", label: "Project Tasks", count: projectPeerTasks.length },
    { key: "subtasks", label: "Assigned Subtasks", count: assignedSubtasks.length },
    { key: "requests", label: "My Submitted Requests", count: createdTasks.length },
  ];

  // ── Main Dashboard View ────────────────────────────────────────
  const currentUser = { userId: currentUserId, role: 'employee' };
  const open = assignedTasks.filter(t => t.status === "Open").length;
  const inProgress = assignedTasks.filter(t => t.status === "Ongoing").length;
  const blocked = assignedTasks.filter(t => t.status === "Blocked").length;
  const onHold = assignedTasks.filter(t => t.status === "On Hold").length;
  const closed = assignedTasks.filter(t => t.status === "Closed").length;
  const dueToday = assignedTasks.filter(t => t.due_date && parseLocalDate(t.due_date).toDateString() === today.toDateString() && isNotClosed(t)).length;
  const overdue = assignedTasks.filter(t => t.due_date && parseLocalDate(t.due_date) < today && isNotClosed(t)).length;

  const taskStats = [
    { label: "Open", value: open, accent: 'bg-blue-500', icon: '📋', filter: "open" },
    { label: "Ongoing", value: inProgress, accent: 'bg-yellow-400', icon: '⚡', filter: "in_progress" },
    { label: "Blocked", value: blocked, accent: 'bg-red-500', icon: '🚫', filter: "blocked" },
    { label: "On Hold", value: onHold, accent: 'bg-orange-400', icon: '⏸', filter: "on_hold" },
    { label: "Closed", value: closed, accent: 'bg-green-500', icon: '✅', filter: "closed" },
    { label: "Due Today", value: dueToday, accent: 'bg-amber-400', icon: '📅', filter: "due_today" },
    { label: "Overdue", value: overdue, accent: 'bg-red-700', icon: '⚠️', filter: "overdue" },
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 max-w-7xl mx-auto">
          <Watermark />

          {/* ── Header ── */}
          <div className="flex items-center gap-3 pb-6 mb-6 border-b border-gray-200">
            <div className="w-1 h-10 rounded-full bg-[#7AA33E]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Employee Dashboard</h1>
              <p className="text-gray-500 text-sm mt-0.5">Your assigned tasks and subtasks from your manager</p>
            </div>
          </div>

          {/* ── Tab Bar ── */}
          <div className="flex gap-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-1.5 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  sessionStorage.setItem('employee_dashboard_tab', tab.key);
                  setActiveTab(tab.key);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition ${activeTab === tab.key
                    ? "bg-[#7AA33E] text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-100"
                  }`}
              >
                <span className="truncate">{tab.label}</span>
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${activeTab === tab.key ? "bg-white/25 text-white" : "bg-gray-100 text-gray-600"
                  }`}>{tab.count}</span>
              </button>
            ))}
          </div>

          {/* ── Assigned Tasks Section ── */}
          <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6 ${activeTab !== "assigned" ? "hidden" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-gray-800">Assigned Tasks</h2>
                <p className="text-xs text-gray-400 mt-0.5">{filtered.length} task{filtered.length !== 1 ? 's' : ''} found</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    value={assignedSearch}
                    onChange={e => { setAssignedSearch(e.target.value); setAssignedPage(1); }}
                    placeholder="Search tasks..."
                    className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-44"
                  />
                </div>
                <select
                  value={assignedStatus}
                  onChange={e => { setAssignedStatus(e.target.value); setAssignedPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="on_hold">On Hold</option>
                  <option value="closed">Closed</option>
                  <option value="due_today">Due Today</option>
                  <option value="overdue">Overdue</option>
                </select>
                <select
                  value={assignedPriority}
                  onChange={e => { setAssignedPriority(e.target.value); setAssignedPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
                <select
                  value={assignedSort}
                  onChange={e => { setAssignedSort(e.target.value); setAssignedPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="due_asc">Due Date ↑</option>
                  <option value="due_desc">Due Date ↓</option>
                  <option value="title_az">Title A–Z</option>
                  <option value="title_za">Title Z–A</option>
                </select>
                <button
                  onClick={fetchAllTasks}
                  title="Refresh"
                  className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {/* ── Status Filter Cards ── */}
            <div className="overflow-x-auto mb-4">
              <StatusFilterCards
                stats={taskStats}
                activeFilter={assignedStatus}
                onFilter={f => { setAssignedStatus(f); setAssignedPage(1); }}
                variant="accent"
              />
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-4 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Loading tasks...</p>
              </div>
            ) : pagedAssigned.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <span className="text-5xl">📭</span>
                <p className="text-gray-500 font-medium text-sm">No assigned tasks found</p>
                <p className="text-gray-400 text-xs">Tasks assigned to you by your manager will appear here</p>
              </div>
            ) : (
              <>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                        <th className="text-left py-3 px-4">#</th>
                        <th className="text-left py-3 px-4">Title</th>
                        <th className="text-left py-3 px-4">Priority</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Due Date</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagedAssigned.map((task) => (
                        <tr key={task.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                          <td className="py-3 px-4 text-gray-400 font-mono text-xs">{task.id}</td>
                          <td className="py-3 px-4 font-medium text-gray-800 max-w-[240px]">
                            <p className="truncate">{task.title}</p>
                            {task.task_type_code === "ADMIN_TASK" && (
                              <span className="mt-0.5 inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700">Org</span>
                            )}
                            {task.task_type_code === "ADMIN_REQUEST" && (
                              <span className="mt-0.5 inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">Mgmt Request</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityBadge(task.priority)}`}>{task.priority}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge(task.status)}`}>
                              {task.status || "Open"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                            {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                          </td>
                          <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => navigate(`/tasks/${task.id}`)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] transition"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {assignedTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                    <span>Showing {(assignedPage - 1) * ASSIGNED_PAGE_SIZE + 1}–{Math.min(assignedPage * ASSIGNED_PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                    <div className="flex gap-2">
                      <button onClick={() => setAssignedPage(p => Math.max(1, p - 1))} disabled={assignedPage === 1} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition">← Prev</button>
                      <button onClick={() => setAssignedPage(p => Math.min(assignedTotalPages, p + 1))} disabled={assignedPage === assignedTotalPages} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition">Next →</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Project Tasks Tab ── */}
          <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6 ${activeTab !== "project" ? "hidden" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-gray-800">Project Tasks</h2>
                <p className="text-xs text-gray-400 mt-0.5">All tasks in your project</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    value={peerSearch}
                    onChange={e => { setPeerSearch(e.target.value); setPeerPage(1); }}
                    placeholder="Search tasks..."
                    className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-44"
                  />
                </div>
                <select
                  value={peerStatusFilter}
                  onChange={e => { setPeerStatusFilter(e.target.value); setPeerPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="Open">Open</option>
                  <option value="Ongoing">In Progress</option>
                  <option value="Blocked">Blocked</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Closed">Closed</option>
                </select>
                <select
                  value={peerPriority}
                  onChange={e => { setPeerPriority(e.target.value); setPeerPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
                <select
                  value={peerSort}
                  onChange={e => { setPeerSort(e.target.value); setPeerPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="due_asc">Due Date ↑</option>
                  <option value="due_desc">Due Date ↓</option>
                  <option value="title_az">Title A–Z</option>
                  <option value="title_za">Title Z–A</option>
                </select>
                <button
                  onClick={fetchProjectPeerTasks}
                  title="Refresh"
                  className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {loadingPeerTasks ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-4 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Loading project tasks...</p>
              </div>
            ) : (() => {
              const filteredPeer = projectPeerTasks
                .filter(t => !peerSearch || t.title.toLowerCase().includes(peerSearch.toLowerCase()) || (t.assignee_name || '').toLowerCase().includes(peerSearch.toLowerCase()))
                .filter(t => !peerStatusFilter || t.status === peerStatusFilter)
                .filter(t => !peerPriority || t.priority === peerPriority)
                .slice().sort(SORT_FN[peerSort] || SORT_FN.newest);
              const peerTotalPages = Math.max(1, Math.ceil(filteredPeer.length / PEER_PAGE_SIZE));
              const pagedPeer = filteredPeer.slice((peerPage - 1) * PEER_PAGE_SIZE, peerPage * PEER_PAGE_SIZE);
              return filteredPeer.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <span className="text-5xl">🗂️</span>
                  <p className="text-gray-500 font-medium text-sm">
                    {projectPeerTasks.length === 0 ? "No project tasks found" : "No tasks match your filters"}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {projectPeerTasks.length === 0
                      ? "You are not currently a member of any project, or no peer tasks exist yet"
                      : "Try adjusting your search or status filter"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                          <th className="text-left py-3 px-4">#</th>
                          <th className="text-left py-3 px-4">Title</th>
                          <th className="text-left py-3 px-4">Assigned To</th>
                          <th className="text-left py-3 px-4">Priority</th>
                          <th className="text-left py-3 px-4">Status</th>
                          <th className="text-left py-3 px-4">Due Date</th>
                          <th className="text-left py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagedPeer.map((task) => (
                          <tr key={task.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                            <td className="py-3 px-4 text-gray-400 font-mono text-xs">{task.id}</td>
                            <td className="py-3 px-4 font-medium text-gray-800 max-w-[200px] truncate">{task.title}</td>
                            <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                              {task.assignee_name?.trim() || <span className="text-gray-400 italic">Unassigned</span>}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityBadge(task.priority)}`}>{task.priority || "—"}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge(task.status)}`}>{task.status || "Open"}</span>
                            </td>
                            <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                              {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                            </td>
                            <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/tasks/${task.id}`)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] transition"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {peerTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                      <span>Showing {(peerPage - 1) * PEER_PAGE_SIZE + 1}–{Math.min(peerPage * PEER_PAGE_SIZE, filteredPeer.length)} of {filteredPeer.length}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setPeerPage(p => Math.max(1, p - 1))} disabled={peerPage === 1} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition">← Prev</button>
                        <button onClick={() => setPeerPage(p => Math.min(peerTotalPages, p + 1))} disabled={peerPage === peerTotalPages} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition">Next →</button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* ── Assigned Subtasks Section ── */}
          <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6 ${activeTab !== "subtasks" ? "hidden" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-base font-bold text-gray-800">Assigned Subtasks</h2>
                <p className="text-xs text-gray-400 mt-0.5">{filteredSubtasks.length} subtask{filteredSubtasks.length !== 1 ? 's' : ''} found</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    value={subtaskSearch}
                    onChange={e => { setSubtaskSearch(e.target.value); setSubtaskPage(1); }}
                    placeholder="Search subtasks..."
                    className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-44"
                  />
                </div>
                <select
                  value={subtaskStatusFilter}
                  onChange={e => { setSubtaskStatusFilter(e.target.value); setSubtaskPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="Open">Open</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Blocked">Blocked</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Closed">Closed</option>
                </select>
                <select
                  value={subtaskPriorityFilter}
                  onChange={e => { setSubtaskPriorityFilter(e.target.value); setSubtaskPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">All Priority</option>
                  <option value="Critical">Critical</option>
                  <option value="Major">Major</option>
                  <option value="Minor">Minor</option>
                </select>
                <select
                  value={subtaskSort}
                  onChange={e => { setSubtaskSort(e.target.value); setSubtaskPage(1); }}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="due_asc">Due Date ↑</option>
                  <option value="due_desc">Due Date ↓</option>
                  <option value="title_az">Title A–Z</option>
                  <option value="title_za">Title Z–A</option>
                </select>
                <button
                  onClick={fetchSubtaskData}
                  title="Refresh"
                  className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {loadingSubtasks ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-4 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Loading subtasks...</p>
              </div>
            ) : (
              <>
                {/* ── Subtask Stats ── */}
                {subtaskStats && (() => {
                  const sstats = [
                    { label: 'Total', value: subtaskStats.total, accent: 'bg-gray-400', icon: '📊', filter: '' },
                    { label: 'Open', value: subtaskStats.open, accent: 'bg-blue-500', icon: '📋', filter: 'Open' },
                    { label: 'Ongoing', value: subtaskStats.ongoing || 0, accent: 'bg-yellow-400', icon: '⚡', filter: 'Ongoing' },
                    { label: 'Blocked', value: subtaskStats.blocked || 0, accent: 'bg-red-500', icon: '🚫', filter: 'Blocked' },
                    { label: 'Closed', value: subtaskStats.closed, accent: 'bg-green-500', icon: '✅', filter: 'Closed' },
                    { label: 'Due Today', value: subtaskStats.dueToday, accent: 'bg-amber-400', icon: '📅', filter: '' },
                    { label: 'Overdue', value: subtaskStats.overdue, accent: 'bg-red-700', icon: '⚠️', filter: '' },
                  ];
                  return (
                    <StatusFilterCards
                      stats={sstats}
                      activeFilter={subtaskStatusFilter}
                      onFilter={(f) => { setSubtaskStatusFilter(f); setSubtaskPage(1); }}
                      variant="accent"
                    />
                  );
                })()}

                {filteredSubtasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <span className="text-5xl">🗂️</span>
                    <p className="text-gray-500 font-medium text-sm">No assigned subtasks found</p>
                    <p className="text-gray-400 text-xs">Subtasks assigned to you will appear here</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                            <th className="text-left py-3 px-4">#</th>
                            <th className="text-left py-3 px-4">Subtask</th>
                            <th className="text-left py-3 px-4">Parent Task</th>
                            <th className="text-left py-3 px-4">Project</th>
                            <th className="text-left py-3 px-4">Priority</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Due Date</th>
                            <th className="text-left py-3 px-4">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {pagedSubtasks.map((s) => {
                            const isOverdue = s.due_date && s.status !== 'Closed' && new Date(s.due_date) < new Date();
                            return (
                              <tr key={s.id} className={`transition cursor-pointer ${isOverdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                                onClick={() => setActiveSubtask(s)}>
                                <td className="py-3 px-4 text-gray-400 font-mono text-xs">{s.id}</td>
                                <td className="py-3 px-4 font-medium text-gray-800 max-w-[180px] truncate">{s.title}</td>
                                <td className="py-3 px-4 text-gray-500 max-w-[160px] truncate">{s.parent_task_title || '—'}</td>
                                <td className="py-3 px-4 text-gray-500 max-w-[140px] truncate">{s.project_name || '—'}</td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                                      s.priority === 'Major' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>{s.priority}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.status === 'Open' ? 'bg-blue-100 text-blue-700' :
                                      s.status === 'Ongoing' ? 'bg-yellow-100 text-yellow-700' :
                                        s.status === 'Blocked' ? 'bg-red-100 text-red-700' :
                                          s.status === 'On Hold' ? 'bg-orange-100 text-orange-700' :
                                            'bg-gray-100 text-gray-500'
                                    }`}>{s.status}</span>
                                </td>
                                <td className={`py-3 px-4 whitespace-nowrap text-sm ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                  {s.due_date ? new Date(s.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </td>
                                <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => setActiveSubtask(s)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] transition"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    View
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {subtaskTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                        <span>Showing {(subtaskPage - 1) * SUBTASK_PAGE_SIZE + 1}–{Math.min(subtaskPage * SUBTASK_PAGE_SIZE, filteredSubtasks.length)} of {filteredSubtasks.length}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSubtaskPage(p => Math.max(1, p - 1))}
                            disabled={subtaskPage === 1}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition"
                          >← Prev</button>
                          <button
                            onClick={() => setSubtaskPage(p => Math.min(subtaskTotalPages, p + 1))}
                            disabled={subtaskPage === subtaskTotalPages}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition"
                          >Next →</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* ── My Submitted Requests Tab ── */}
          <div className={`mb-6 ${activeTab !== "requests" ? "hidden" : ""}`}>

            {/* ── New Request Form (collapsible) ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-gray-800">New Request</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Submit a task request to the admin for review</p>
                </div>
                <button
                  onClick={() => { setShowRequestForm(v => !v); setCreateMsg(""); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition ${showRequestForm
                      ? "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                      : "bg-[#7AA33E] text-white border-[#7AA33E] hover:bg-[#6a9335]"
                    }`}
                >
                  {showRequestForm ? "✕ Cancel" : "+ New Request"}
                </button>
              </div>

              {showRequestForm && (
                <form onSubmit={async (e) => { const ok = await handleCreateTask(e); if (ok) setShowRequestForm(false); }} className="mt-6 flex flex-col gap-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Title <span className="text-red-400">*</span></label>
                      <input
                        value={newTask.title}
                        onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                        placeholder="e.g. Need access to reporting tool"
                        className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</label>
                        <span className="text-xs text-gray-400">Optional</span>
                      </div>
                      <input
                        type="date"
                        value={newTask.due_date}
                        min={getMinDate()}
                        onChange={e => {
                          const val = e.target.value;
                          const err = validateDueDate(val);
                          if (err) { setCreateMsg(err); return; }
                          setCreateMsg("");
                          setNewTask({ ...newTask, due_date: val });
                        }}
                        className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description <span className="text-red-400">*</span></label>
                    <textarea
                      value={newTask.description}
                      onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                      placeholder="Describe what you need and why..."
                      className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none transition"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</label>
                    <div className="grid grid-cols-3 gap-2">
                      {PRIORITIES.map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setNewTask({ ...newTask, priority: p })}
                          className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition ${newTask.priority === p
                              ? p === "High" ? "border-red-400 bg-red-50 text-red-700"
                                : p === "Medium" ? "border-yellow-400 bg-yellow-50 text-yellow-700"
                                  : "border-[#7AA33E] bg-[#f4f9ec] text-[#5a8a2a]"
                              : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
                            }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  {createMsg && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm font-medium">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      {createMsg}
                    </div>
                  )}
                  <div className="flex gap-3 pt-1 border-t border-gray-100">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
                      ) : "Submit Request"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowRequestForm(false); setCreateMsg(""); }}
                      className="px-6 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* ── Requests List ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-base font-bold text-gray-800">Submitted Requests</h2>
                  <p className="text-xs text-gray-400 mt-0.5">{filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                    </svg>
                    <input
                      type="text"
                      value={reqSearch}
                      onChange={e => { setReqSearch(e.target.value); setReqPage(1); }}
                      placeholder="Search requests..."
                      className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-44"
                    />
                  </div>
                  <select
                    value={reqStatusFilter}
                    onChange={e => { setReqStatusFilter(e.target.value); setReqPage(1); }}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                  >
                    <option value="">All Status</option>
                    <option value="Open">Open</option>
                    <option value="Ongoing">In Progress</option>
                    <option value="Blocked">Blocked</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Closed">Closed</option>
                  </select>
                  <select
                    value={reqPriorityFilter}
                    onChange={e => { setReqPriorityFilter(e.target.value); setReqPage(1); }}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                  >
                    <option value="">All Priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                  <select
                    value={reqSort}
                    onChange={e => { setReqSort(e.target.value); setReqPage(1); }}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="due_asc">Due Date ↑</option>
                    <option value="due_desc">Due Date ↓</option>
                    <option value="title_az">Title A–Z</option>
                    <option value="title_za">Title Z–A</option>
                  </select>
                  <button
                    onClick={fetchAllTasks}
                    title="Refresh"
                    className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>

              {createdTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <span className="text-5xl">📭</span>
                  <p className="text-gray-500 font-medium text-sm">No requests submitted yet</p>
                  <p className="text-gray-400 text-xs">Click "New Request" above to submit a task request to the admin</p>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <span className="text-5xl">🔍</span>
                  <p className="text-gray-500 font-medium text-sm">No requests match your filters</p>
                  <p className="text-gray-400 text-xs">Try adjusting your search, status, or priority filter</p>
                </div>
              ) : (
                <>
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                          <th className="text-left py-3 px-4">Title</th>
                          <th className="text-left py-3 px-4">Priority</th>
                          <th className="text-left py-3 px-4">Status</th>
                          <th className="text-left py-3 px-4">Due Date</th>
                          <th className="text-left py-3 px-4">Created</th>
                          <th className="text-left py-3 px-4">Assigned Admin</th>
                          <th className="text-left py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagedRequests.map((task) => (
                          <tr key={task.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                            <td className="py-3 px-4 font-medium text-gray-800 max-w-[200px]">
                              <p className="truncate">{task.title}</p>
                              {task.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">{task.description}</p>}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityBadge(task.priority)}`}>{task.priority}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge(task.status)}`}>
                                {task.status || "Open"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                              {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                            </td>
                            <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                              {task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                            </td>
                            <td className="py-3 px-4 text-gray-600 whitespace-nowrap text-sm">
                              {task.assignee_name?.trim() || <span className="text-gray-400 italic">Unassigned</span>}
                            </td>
                            <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/tasks/${task.id}`)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] transition"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {reqTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                      <span>Showing {(reqPage - 1) * REQ_PAGE_SIZE + 1}–{Math.min(reqPage * REQ_PAGE_SIZE, filteredRequests.length)} of {filteredRequests.length}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setReqPage(p => Math.max(1, p - 1))}
                          disabled={reqPage === 1}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition"
                        >← Prev</button>
                        <button
                          onClick={() => setReqPage(p => Math.min(reqTotalPages, p + 1))}
                          disabled={reqPage === reqTotalPages}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium disabled:opacity-40 hover:bg-gray-50 transition"
                        >Next →</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>


        </div>
      </div>

      <CommentsPanel
        open={Boolean(peerCommentsTask)}
        onClose={() => setPeerCommentsTask(null)}
        taskId={peerCommentsTask?.id}
        task={peerCommentsTask}
        currentUserId={currentUserId}
        token={token}
        unreadCount={peerCommentsTask ? (peerUnreadCounts[peerCommentsTask.id] || 0) : 0}
        onUnreadClear={() => peerCommentsTask && setPeerUnreadCounts(s => ({ ...s, [peerCommentsTask.id]: 0 }))}
      />

      {/* Subtask detail drawer — opened from Assigned Subtasks tab */}
      <SubtaskDrawer
        subtask={activeSubtask}
        onClose={() => setActiveSubtask(null)}
        onUpdated={(updated) => {
          setAssignedSubtasks(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
          setActiveSubtask(prev => prev ? { ...prev, ...updated } : null);
        }}
        canEdit={true}
        currentUser={currentUser}
        onViewParentTask={null}
      />
    </>
  );
};

export default EmployeeDashboard;
